import { useAsyncWabunkaExperiencesSearch } from '@/composables/models/wabunka/useAsyncWabunkaExperiencesSearch';
import { isServerRendering } from '@/composables/utils/isServerRendering';
import { useErrorCreator } from '@/composables/utils/wabunka/useErrorCreator';
import { useBuildUrl, useIsSP } from '@/wabunka-src/common/composables';
import { useExperienceListSort } from '@/wabunka-src/common/features/Experience/composables/useExperienceListSort';
import type { ExperienceSortType } from '@/wabunka-src/common/features/Experience/utils/experienceListSort';
import { useFeaturedListQuery, useFilteredFeaturedListQuery } from '@/wabunka-src/common/features/Featured';
import { sendGAWabunkaChangeSortEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/changeSort';
import { sendGAWabunkaSectionSlugViewEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/sectionSlugView';
import { sendGAWabunkaSelectExperienceInListEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/selectExperienceInList';
import { sendGAWabunkaViewExperienceListEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/viewExperienceList';
import { useKeywordSearchModal } from '@/wabunka-src/features/Search/composables/general/useKeywordSearchModal';

interface UseExperienceCollectionParams {
  destination: string;
  theme?: string;
  experiencesPerPage: number;
}

export const useExperienceCollection = async ({ destination, theme = '', experiencesPerPage }: UseExperienceCollectionParams) => {
  // コンテキストに依存する処理は関数の最初（await前）に行う
  const route = useRoute();
  const { buildUrlWithEndSlash } = useBuildUrl();
  const { open: openSearchModal } = useKeywordSearchModal();
  const { createWabunkaError } = useErrorCreator();
  const isSP = useIsSP();
  // await を跨ぐと currentInstance を失い、あとで作る watch がアンマウントしても止まらない。
  // 止まらないと画面を離れたあとも route 由来の並び順に反応して view_experience_list を送ってしまう
  const viewEventScope = effectScope();
  onScopeDispose(() => viewEventScope.stop());

  // エリアとカテゴリに対応するタグクエリを作成
  const destinationTagsQuery = computed<string>(() => destination);
  const themeTagsQuery = computed<string>(() => theme);
  const experienceEventStartAtDatesQuery = computed(() => '');
  const { selectedSortType, experienceListApiSort, handleSortChange: navigateWithSortType, withCurrentSortQuery } = useExperienceListSort(() => route.path);

  // 非同期コンポーザブルを並列で実行し、コンテキストの喪失リスクを最小限にする
  const [featuredQueryResult, experiencesSearchId] = await Promise.all([
    useFeaturedListQuery({ types: ['itinerary'] }),
    useAsyncWabunkaExperiencesSearch({
      experienceEventStartAtDates: experienceEventStartAtDatesQuery,
      destinations: destinationTagsQuery,
      themes: themeTagsQuery,
      slugs: ref([]),
      page: ref(1),
      perPage: ref(experiencesPerPage),
      sort: experienceListApiSort,
    }),
  ]);

  const { featuredList } = featuredQueryResult;
  const { itineraryList } = useFilteredFeaturedListQuery(featuredList, destinationTagsQuery, themeTagsQuery);

  const { experiences, meta, totalCount, hasNoResponse, isLoading, hasError } = experiencesSearchId;

  // 検索APIの取得失敗（エラーなしの null 応答）を「0件」として扱わない（JCAT-5203）。
  // 0件扱いにすると一覧系ページの shouldIndex が false になり、
  // 公開中のページが 200 + noindex で配信されてインデックスから除外される。
  // クライアント側はエッジキャッシュ通貨不一致によるハイドレーション時の一時的 null が
  // 常態的に発生するため throw せず、ページ側の shouldIndex ガードで noindex を抑止する。
  if (isServerRendering() && hasNoResponse.value) {
    throw createWabunkaError({
      statusCode: 503,
      statusMessage: 'Service Unavailable',
      fatal: true,
      customMessage: `experiences search returned null without error: destination=${destination} theme=${theme}`,
    });
  }

  // 初期化フラグ
  const hasInitialized = ref(false);
  const hasItineraryInitialized = ref(false);

  // ローディング状態
  const isLoadingExperiences = computed(() => isLoading.value);

  // 体験件数
  const experienceCount = computed(() => totalCount.value || 0);

  // 初期化完了を監視
  watchEffect(() => {
    if (!isLoadingExperiences.value && !hasInitialized.value) {
      hasInitialized.value = true;
    }
  });

  watchEffect(() => {
    if (itineraryList.value !== undefined && !hasItineraryInitialized.value) {
      hasItineraryInitialized.value = true;
    }
  });

  // 全データ取得完了フラグ
  const allDataInitialized = computed(() => hasInitialized.value && hasItineraryInitialized.value);

  const handleSortChange = (sortType: ExperienceSortType) => {
    sendGAWabunkaChangeSortEvent(sortType);
    navigateWithSortType(sortType);
  };

  // 体験カードクリック時の計測。position は表示中の並びでの順位（1 始まり）。遷移はカード側の NuxtLink が持つ
  const handleExperienceClick = (experienceSlug: string, position: number) => {
    sendGAWabunkaSelectExperienceInListEvent({ sortType: selectedSortType.value, clickedSlug: experienceSlug, position });
  };

  // view_experience_list（一覧→詳細遷移率の分母）。この3面は1ページ目固定・絞り込み無しなので、表示が変わるのは並び順だけ
  const settledListSortType = computed<ExperienceSortType | null>(() =>
    isLoadingExperiences.value || hasError.value || hasNoResponse.value ? null : selectedSortType.value,
  );
  if (import.meta.client) {
    viewEventScope.run(() => {
      // 通貨切替のように表示が同じまま再取得が走ると null を経由して同じ値へ戻るため、直前に送った並び順は送らない
      let sentListSortType: ExperienceSortType | null = null;
      watch(
        settledListSortType,
        (sortType) => {
          if (sortType === null || sortType === sentListSortType) return;
          sentListSortType = sortType;
          sendGAWabunkaViewExperienceListEvent(sortType, totalCount.value);
        },
        { immediate: true },
      );
    });
  }

  // コンタクトボタンクリック時のハンドラ
  const onClickContact = () => {
    navigateTo('/contact/');
  };

  // パートナーボタンクリック時のハンドラ
  const onClickPartner = () => {
    navigateTo('/lp/partner-with-us/');
  };

  // フィーチャードセクション（旅程）のカードクリック時のハンドラ
  const handleFeaturedClick = (type: string, id: string) => {
    sendGAWabunkaSectionSlugViewEvent(type, id, route.name as string);
  };

  // 検索ボタンクリック時のハンドラ
  const handleSearchClick = () => {
    openSearchModal();
  };

  return {
    isSP,
    itineraryList,
    experiences,
    meta,
    totalCount,
    isLoadingExperiences,
    experienceCount,
    allDataInitialized,
    destinationTagsQuery,
    themeTagsQuery,
    experienceEventStartAtDatesQuery,
    handleExperienceClick,
    onClickContact,
    onClickPartner,
    handleFeaturedClick,
    handleSearchClick,
    selectedSortType,
    handleSortChange,
    withCurrentSortQuery,
    buildUrlWithEndSlash,
    hasNoResponse,
  };
};
